import { Injectable } from '@angular/core';
import { Activity, ActivityType } from '@omni/classes/activity/activity.class';
import { IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import {
  ACTIVITY_TYPE_TO_CONFIG_KEY,
  BASE_KEY,
  CalendarActivityColorEntry,
  CalendarActivityType,
  CalendarColorConfig,
  DEFAULT_PALETTE,
  FALLBACK_BASE,
  ResolvedChipStyle,
  SUPPORTED_SCHEMA_VERSION,
} from '@omni/config/calendar/calendar-activity-color.model';
import { CALENDAR_STATUS_KEYS, resolveStatusKey } from '@omni/config/calendar/calendar-status-map';
import { AuthenticationService } from '@omni/services/authentication.service';
import {
  Rgb,
  deriveInk,
  hexToRgb,
  isValidHex,
  relativeLuminance,
  rgbToHex,
} from '@omni/utility/color-derivation.util';

/** Org-level records are matched by name; BU-level records carry a feature code. */
const LEGACY_CONFIG_NAME = 'Calendar.ActivityColors.Configuration';

/** FALLBACK_BASE pre-parsed, for the unreachable case where even that fails to parse.
 *  Keep in step with it. */
const FALLBACK_BASE_RGB: Rgb = { r: 194, g: 230, b: 255 };

/** One activity's resolved colours: a base plus zero or more status colours. */
interface PaletteEntry {
  base?: string;
  statuses: Record<string, string>;
}

/** Config JSON on a record, or null. Module scope: the per-chip path must not rebuild it. */
function rawConfigValue(record: any): string | null {
  const raw = record?.configValue;
  return typeof raw === 'string' && raw.trim().length ? raw : null;
}

/** Status arrives as a number from some classes and a string from others; the maps are
 *  keyed on numbers, so an un-normalised string silently misses every entry. */
function toStatusNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isNaN(value) ? undefined : value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

/**
 * Resolves calendar chip colours, and is the single source for both fill and ink so the
 * two cannot drift. `resolve()` always returns a style - the shipped palette is just the
 * config we ship. See docs/calendar-activity-colors.md.
 */
@Injectable({ providedIn: 'root' })
export class CalendarColorService {
  /** Constructor injection rather than `inject()` so the service can be unit tested by
   *  construction, without an Angular injection context. */
  constructor(private readonly authService: AuthenticationService) {}

  /** Customer overrides, per config key. Empty when nothing is configured. */
  private palette: Record<string, PaletteEntry> = {};
  private loaded = false;

  /** The raw values behind the current parse. Compared on every read so an edited record
   *  re-parses; kept as two fields so nothing has to be concatenated per chip. */
  private lastBuRaw: string | null = null;
  private lastOrgRaw: string | null = null;

  /** The array the records below were found in. A new reference means rescan. */
  private scannedConfigs: unknown[] | null = null;
  /** The matched records, kept so the per-chip read is a property access, not a search. */
  private buRecord: any = null;
  private orgRecord: any = null;

  /** Memoised per `${configKey}:${statusKey}` - a week view renders hundreds of chips
   *  and the ink derivation walks an OKLCH lightness ramp. At most 76 entries. */
  private readonly cache = new Map<string, ResolvedChipStyle>();

  /** Unmapped (type, status) pairs already reported, so telemetry is not spammed. */
  private readonly reportedUnmapped = new Set<string>();

  /** Map a discriminated `ActivityType` to its config key, or null if not renderable. */
  getConfigKey(type: ActivityType): CalendarActivityType | null {
    return ACTIVITY_TYPE_TO_CONFIG_KEY.get(type) ?? null;
  }

  /** Chip styling for an activity; never null. `calendarType` is the discriminated type
   *  from `ActivityService.getActivityTypeForCalendarFilter`. */
  resolve(activity: Activity, calendarType: ActivityType): ResolvedChipStyle {
    this.ensureLoaded();

    const configKey = this.getConfigKey(calendarType) ?? this.getConfigKey(activity?.type);
    if (!configKey) return this.styleFor(null, null);

    const statusKey = this.statusKeyFor(configKey, activity);
    return this.styleFor(configKey, statusKey);
  }

  /** Exposed for the legend (Part 2) and for tests. */
  styleFor(configKey: CalendarActivityType | null, statusKey: string | null): ResolvedChipStyle {
    const cacheKey = `${configKey ?? '-'}:${statusKey ?? '-'}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return cached;

    const fill = this.fillFor(configKey, statusKey);

    // `fill` has been through isValidHex on every path that reaches here; the literal is
    // a last resort so this needs no non-null assertion (banned by .eslintrc).
    const fillRgb = hexToRgb(fill) ?? hexToRgb(FALLBACK_BASE) ?? FALLBACK_BASE_RGB;
    const inkRgb = deriveInk(fillRgb);

    // One derivation, two uses. A lighter ink means the fill was too dark to darken, and
    // any such fill is itself readable on the card, so no second walk is needed. See the
    // doc for why that always holds.
    const inkIsLighter = relativeLuminance(inkRgb) > relativeLuminance(fillRgb);
    const resolved: ResolvedChipStyle = {
      fill,
      ink: rgbToHex(inkRgb),
      inkOnCard: inkIsLighter ? fill : rgbToHex(inkRgb),
    };

    this.cache.set(cacheKey, resolved);
    return resolved;
  }

  // -------------------------------------------------------------------------
  // Resolution
  // -------------------------------------------------------------------------

  /** Configured status colour -> configured base -> shipped default -> fallback. Per
   *  entry, so one bad colour costs only that entry. */
  private fillFor(configKey: CalendarActivityType | null, statusKey: string | null): string {
    if (!configKey) return FALLBACK_BASE;

    const entry = this.palette[configKey];

    if (statusKey) {
      const configuredStatus = entry?.statuses?.[statusKey];
      if (configuredStatus) return configuredStatus;
    }

    if (entry?.base) return entry.base;

    return DEFAULT_PALETTE[configKey] ?? FALLBACK_BASE;
  }

  private statusKeyFor(configKey: CalendarActivityType, activity: Activity): string | null {
    const status = this.statusFor(configKey, activity);
    const key = resolveStatusKey(configKey, activity?.state, status);

    if (!key && status !== undefined) {
      this.reportUnmapped(configKey, status, activity?.state);
    }
    return key;
  }

  /**
   * The value a type's `byStatus` map is keyed on. Per type because there is no one
   * field: `Activity.status` is `raw['statuscode']` unparsed, while Time Off and Medical
   * Inquiry hold theirs elsewhere - reading `status` left both maps dead.
   */
  private statusFor(configKey: CalendarActivityType, activity: Activity): number | undefined {
    const a = activity as any;

    switch (configKey) {
      // No fallback: an email's statuscode is a different option set from EmailStatusCodes.
      case CalendarActivityType.Message:
        return toStatusNumber(a?.emailStatus);

      // These fall back safely - the base field comes from the same `raw['statuscode']`.
      case CalendarActivityType.KitBooking:
        return toStatusNumber(a?.rawData?.indskr_status) ?? toStatusNumber(a?.status);
      case CalendarActivityType.TimeOff:
        return toStatusNumber(a?.statuscode) ?? toStatusNumber(a?.status);
      case CalendarActivityType.MedicalInquiry:
        return toStatusNumber(a?.statusCode) ?? toStatusNumber(a?.status);

      default:
        return toStatusNumber(a?.status);
    }
  }

  private reportUnmapped(configKey: CalendarActivityType, status: number, state?: number): void {
    const key = `${configKey}:${status}`;
    if (this.reportedUnmapped.has(key)) return;
    this.reportedUnmapped.add(key);
    console.warn(
      `[CalendarColorService] Unmapped status for ${configKey}: statuscode=${status}, statecode=${state}. ` +
      `The activity renders in its base colour. Add it to STATUS_MAP in calendar-status-map.ts.`,
    );
  }

  // -------------------------------------------------------------------------
  // Config loading
  // -------------------------------------------------------------------------

  /** Drop everything parsed, so the next read starts from the current user's config. */
  private resetParsed(): void {
    this.loaded = false;
    this.lastBuRaw = null;
    this.lastOrgRaw = null;
    this.forgetRecords();
    this.palette = {};
    this.cache.clear();
    this.reportedUnmapped.clear();
  }

  private ensureLoaded(): void {
    const configs = this.authService?.user?.ioConfigurations as unknown[] | undefined;

    // Do not latch before the configs exist, or a calendar built before sync pins an
    // empty palette for the session. Clear first: a different user may have signed in.
    if (!Array.isArray(configs) || !configs.length) {
      if (this.loaded) this.resetParsed();
      else this.forgetRecords();
      return;
    }

    // Re-read every call so an edited record is picked up without a restart; the parse
    // below only runs when the value differs.
    this.refreshRecords(configs);
    const businessUnit = rawConfigValue(this.buRecord);
    const organisation = rawConfigValue(this.orgRecord);
    if (this.loaded && businessUnit === this.lastBuRaw && organisation === this.lastOrgRaw) return;

    this.palette = {};
    this.cache.clear();
    this.reportedUnmapped.clear();
    this.loaded = true;
    this.lastBuRaw = businessUnit;
    this.lastOrgRaw = organisation;

    if (!businessUnit && !organisation) {
      console.info(
        '[CalendarColorService] No calendar-colour config record found ' +
        `(looked for featureCode "${IoConfigurationFeatureCodes.CALENDAR_ACTIVITY_COLORS}", ` +
        `then configName "${IoConfigurationFeatureCodes.CALENDAR_ACTIVITY_COLORS}", ` +
        `then configName "${LEGACY_CONFIG_NAME}"); using the shipped palette.`,
      );
      return;
    }

    // BU beats org PER ACTIVITY TYPE, entry taken whole. Statuses are not merged across
    // records, or a colour on screen would come from two places at once.
    const orgPalette = this.parse(organisation, 'organisation');
    const buPalette = this.parse(businessUnit, 'business unit');
    this.palette = { ...orgPalette, ...buPalette };

    const configured = Object.keys(this.palette).length;
    const statuses = Object.values(this.palette)
      .reduce((sum, entry) => sum + Object.keys(entry.statuses).length, 0);
    console.info(
      `[CalendarColorService] palette loaded: ${configured} activity type(s) configured, ` +
      `${statuses} status colour(s). Unconfigured types use the shipped palette.`,
    );
  }

  /** Parse one record's `configValue` into per-type entries. Returns {} for anything
   *  unusable, having said why - the shipped palette then applies. */
  private parse(raw: string | null, label: string): Record<string, PaletteEntry> {
    if (!raw) return {};

    let parsed: CalendarColorConfig;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      console.error(
        `[CalendarColorService] ${label} configValue is not valid JSON; ignoring that record.`,
        error,
      );
      return {};
    }

    if (!parsed || typeof parsed !== 'object') return {};

    // Absent schemaVersion means 1. Present but unrecognised means this build does not
    // understand the shape, so ignore the record rather than half-applying it.
    if (parsed.schemaVersion !== undefined && parsed.schemaVersion !== SUPPORTED_SCHEMA_VERSION) {
      console.warn(
        `[CalendarColorService] ${label} record has unsupported schemaVersion ` +
        `${parsed.schemaVersion} (this build supports ${SUPPORTED_SCHEMA_VERSION}); ignoring it.`,
      );
      return {};
    }

    // The record is a flat map of activity key -> entry. An earlier draft nested it under
    // an `activities` wrapper; that is still accepted, and the two cannot be confused
    // because "activities" is not itself an activity key.
    const wrapper = parsed.activities;
    const activities = (wrapper && typeof wrapper === 'object')
      ? wrapper as Record<string, CalendarActivityColorEntry>
      : parsed as Record<string, CalendarActivityColorEntry>;

    const result: Record<string, PaletteEntry> = {};

    for (const key of Object.keys(activities)) {
      // Unknown keys are ignored, not errors. hasOwnProperty rather than `in`: `in`
      // walks the prototype chain, so "toString" would pass.
      if (!Object.prototype.hasOwnProperty.call(CalendarActivityType, key)) continue;

      const entry = activities[key];
      if (!entry || typeof entry !== 'object') continue;

      const parsedEntry = this.parseEntry(key as CalendarActivityType, entry, label);
      if (parsedEntry.base || Object.keys(parsedEntry.statuses).length) {
        result[key] = parsedEntry;
      }
    }

    return result;
  }

  private parseEntry(
    key: CalendarActivityType,
    entry: CalendarActivityColorEntry,
    label: string,
  ): PaletteEntry {
    const legalStatusKeys = CALENDAR_STATUS_KEYS[key] ?? [];
    const parsed: PaletteEntry = { statuses: {} };

    for (const field of Object.keys(entry)) {
      const value = entry[field];

      if (field === BASE_KEY) {
        if (isValidHex(value)) {
          parsed.base = value.trim();
        } else if (value !== undefined) {
          console.warn(`[CalendarColorService] ${label}: ignoring invalid base colour for ${key}: ${value}`);
        }
        continue;
      }

      // A status key that is not legal for this type can never match a real activity,
      // so it is dropped here rather than sitting in memory looking configured.
      if (!legalStatusKeys.includes(field)) {
        console.warn(
          `[CalendarColorService] ${label}: ignoring unknown status key "${field}" for ${key}. ` +
          `Accepted: ${legalStatusKeys.join(', ')}.`,
        );
        continue;
      }

      if (isValidHex(value)) {
        parsed.statuses[field] = value.trim();
      } else if (value !== undefined) {
        console.warn(
          `[CalendarColorService] ${label}: ignoring invalid colour for ${key}.${field}: ${value}. ` +
          'That status falls back to the base colour.',
        );
      }
    }

    return parsed;
  }

  /** Point `buRecord` / `orgRecord` at the current array. The BU record is matched on
   *  `featureCode`, which is what the BU-level FetchXML filters on; the org one by name. */
  private refreshRecords(configs: any[]): void {
    // Runs once per chip, so it must not scan once per chip. A sync replaces the whole
    // array (mapUser builds a new User), so identity is the signal; the caller still
    // re-reads configValue, catching an in-place edit at O(1).
    if (configs === this.scannedConfigs) return;

    const featureCode = IoConfigurationFeatureCodes.CALENDAR_ACTIVITY_COLORS;
    this.scannedConfigs = configs;
    this.buRecord = configs.find(c => c?.featureCode === featureCode) ?? null;
    this.orgRecord = configs.find(c => c?.configName === featureCode)
      ?? configs.find(c => c?.configName === LEGACY_CONFIG_NAME)
      ?? null;
  }

  private forgetRecords(): void {
    this.scannedConfigs = null;
    this.buRecord = null;
    this.orgRecord = null;
  }
}
